
import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import DynamicTitle from '../components/DynamicTitle';
import SEOStructuredData from '../components/SEOStructuredData';

const AmazonFBA = () => {
  useEffect(() => {
    console.log("User visited Amazon FBA page");
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <DynamicTitle 
        title="My Amazon FBA Rollercoaster - Big Dreams, Hard Lessons, and Finding a New Path"
        description="Aram AI's complete Amazon FBA journey: from losing $3K on Aire Libre to building six-figure qbottle brand. Lessons learned from failures and successes."
        keywords="Amazon FBA, e-commerce, qbottle, Aire Libre, Amazon seller, private label, entrepreneurship, business failure, startup story"
        canonicalUrl="https://aramb.am/amazon-fba"
      />
      <SEOStructuredData 
        type="article"
        title="My Amazon FBA Rollercoaster: Big Dreams, Hard Lessons, and Finding a New Path"
        description="The complete Amazon FBA journey from failed first product to building a six-figure water bottle brand."
        url="https://aramb.am/amazon-fba"
        image="https://aramb.am/lovable-uploads/c4597387-65d7-47ba-9f0c-03a8ee1f7d18.png"
        author="Aram AI"
        datePublished="2024-12-17"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading glow-text">
            My Amazon FBA Rollercoaster: Big Dreams, Hard Lessons, and Finding a New Path
          </h1>
        </div>

        {/* Hero Image */}
        <div className="mb-12">
          <AspectRatio ratio={16/9} className="overflow-hidden rounded-lg">
            <img 
              src="/lovable-uploads/c4597387-65d7-47ba-9f0c-03a8ee1f7d18.png" 
              alt="Drinking from qbottle during Portugal photoshoot" 
              className="w-full h-full object-cover"
            />
          </AspectRatio>
          <p className="text-center text-sm text-gray-400 mt-3 italic">
            This is me drinking from qbottle when we were doing shooting in Portugal - the water bottle brand I built with my investor partner, growing it to six-figure revenues.
          </p>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto mb-24">
          <article className="glass-panel rounded-2xl overflow-hidden">
            <div className="p-12 space-y-8">
              <div className="prose prose-invert prose-xl max-w-none leading-relaxed">
                <p className="text-xl leading-[1.8] text-gray-200 font-light mb-8 first-letter:text-6xl first-letter:font-bold first-letter:text-purple-300 first-letter:float-left first-letter:mr-3 first-letter:mt-1">
                  After my initial experiences on Upwork, I knew I wanted to build my own business. I started looking into different opportunities that were popular at the time – things like dropshipping and even learning programming. After a lot of research, I came across the Amazon FBA (Fulfillment by Amazon) business model. I learned about dropshipping on Amazon, the wholesale model, and Private Label. The idea of creating my own brand with Private Label really interested me. I decided to dive deep, bought a course, and learned all I could.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Taking the Plunge: The "Aire Libre" Venture</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  I started doing market research. My first idea was for a product I called "Jointey" – it was for rolling paper cones. I did some initial work on it, but then I changed my mind. I thought I found something better: air quality monitors. I came up with a brand name, "Aire Libre," found a supplier, and designed the branding and packaging.
                </p>

                {/* Aire Libre Logo */}
                <div className="mb-8 flex justify-center">
                  <div className="bg-white/5 p-8 rounded-xl border border-blue-500/30 max-w-sm">
                    <img 
                      src="/lovable-uploads/e40ac29b-3fce-4a18-ad4f-aad6842e275c.png" 
                      alt="Aire Libre brand logo" 
                      className="w-full h-auto"
                    />
                    <p className="text-center text-sm text-gray-400 mt-4 italic">
                      the Aire Libre logo
                    </p>
                  </div>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  This felt like the real deal. I opened an Amazon account and placed an order for the air quality monitors. I had about $13,000 in savings at that moment, and I decided to invest it all into this venture. For a while, I even left my ongoing Upwork engagements to focus entirely on this launch.
                </p>

                <div className="text-center my-10">
                  <div className="inline-block bg-gradient-to-r from-green-600/20 to-blue-600/20 p-8 rounded-xl border border-green-500/30">
                    <p className="text-2xl font-semibold text-green-200 mb-4">Initial Success</p>
                    <p className="text-lg text-gray-300">First 3 months: <span className="font-bold text-white">$10,000-$12,000</span> monthly revenue</p>
                    <p className="text-sm text-gray-400 mt-2">540 units sold in the first batch</p>
                  </div>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  The items arrived, I launched everything on Amazon, and soon enough, I got my first sales! It was incredibly exciting. For the first three months, "Aire Libre" was making $10,000 to $12,000 in revenue per month. It felt like I was onto something big.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">The Cracks Begin to Show</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  However, success came with challenges. The biggest issue was with the product's mobile application – it was buggy. Many customers started writing bad reviews because of it. This made it very hard to keep the sales level up, and the negative reviews also meant I had to spend more on PPC (Pay-Per-Click advertising) just to stay visible, which ate into the margins. On top of that, many people were returning the item because of the app problems.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  Despite these issues, after selling my first batch of 540 units, I went ahead and ordered a second batch. This is where I made a critical mistake: I didn't do a proper quality check with the supplier in China before they shipped the goods. When the second batch arrived, it was full of defective units. Not damaged in transit, but simply not working correctly.
                </p>

                <div className="bg-red-900/20 border-l-4 border-red-400 pl-6 py-4 my-8 rounded-r-lg">
                  <h3 className="text-2xl font-bold text-red-200 mb-4">A Difficult End to a Promising Start</h3>
                  <p className="text-lg leading-[1.8] text-gray-200 mb-4">
                    That period was incredibly tough. I was dealing with constant returns from the first batch, a flood of bad reviews, poor PPC performance, and now a new batch of inventory that was largely unusable. Eventually, because of all the bad reviews, Amazon suspended my product listing.
                  </p>
                  <p className="text-lg leading-[1.8] text-gray-200">
                    After about three intense months of selling and struggling, I had to make the hard decision to stop the business. I withdrew the balance that was left in the account. In the end, I lost around $3,000 of my initial investment.
                  </p>
                </div>

                <h2 className="text-3xl font-bold mb-6 text-white">From Setback to a New Strategy: Back to Upwork, But Different</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  After that experience, I had to figure out what to do next. I wasn't ready to try building another brand from scratch right away, especially since I had no steady income at that point. But I had gained a massive amount of hands-on experience with Amazon FBA – the good, the bad, and the ugly. My Upwork profile already looked good from my previous work, so I decided to pivot.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  I rebranded my profile as an "Amazon FBA Expert." My goal was to help other businesses succeed and, importantly, help them avoid the painful mistakes I had made. I learned about all aspects of Amazon FBA businesses during my journey, not just one or two things. So, I positioned myself as an FBA generalist.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Helping Others Succeed: My Time as an FBA Consultant</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  Just like when I first started on Upwork, I began looking for clients, but this time, I was specifically targeting Amazon FBA brands. It was surprisingly fast and easy to find opportunities. I started working with some very good brands and interesting people from all over the world, building good, long-term relationships that lasted for about a year and a half. Many of these were seven-figure businesses, and I was responsible for managing their marketplace accounts, often entire country portfolios, helping them increase their sales and overall revenue.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">A New Venture: Building qbottle and "Fulfilled by Us"</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  During this time as a consultant, a Russian investor based in the US approached me. He offered to invest if I would build an Amazon FBA company with him. We decided to work together. He provided the investment, and I took on building the company from the ground up. We created "qbottle" a brand for stainless steel water bottles.
                </p>

                {/* qbottle Logo */}
                <div className="mb-8 flex justify-center">
                  <div className="bg-white/5 p-8 rounded-xl border border-blue-500/30 max-w-sm">
                    <img 
                      src="/lovable-uploads/188429aa-41d0-4d85-b17a-879a1b2b142b.png" 
                      alt="qbottle brand logo" 
                      className="w-full h-auto"
                    />
                    <p className="text-center text-sm text-gray-400 mt-4 italic">
                      the qbottle brand logo
                    </p>
                  </div>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  While building qbottle, I also started an agency called "Fulfilled by Us." With my small team, we helped other businesses, mostly with marketing and account management related to their e-commerce operations.
                </p>

                {/* Fulfilled by Us Logo */}
                <div className="mb-8 flex justify-center">
                  <div className="bg-white/5 p-8 rounded-xl border border-orange-500/30 max-w-sm">
                    <img 
                      src="/lovable-uploads/1b90d7e3-c8dc-4800-a0fd-db325c456d62.png" 
                      alt="Fulfilled by Us agency logo" 
                      className="w-full h-auto"
                    />
                    <p className="text-center text-sm text-gray-400 mt-4 italic">
                      the "fulfilled by us" agency logo
                    </p>
                  </div>
                </div>

                <div className="text-center my-10">
                  <div className="inline-block bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-8 rounded-xl border border-blue-500/30">
                    <p className="text-2xl font-semibold text-blue-200 mb-4">qbottle Success</p>
                    <p className="text-lg text-gray-300">Grew to <span className="font-bold text-white">six-figure revenues</span></p>
                    <p className="text-sm text-gray-400 mt-2">1.5 years of partnership growth</p>
                  </div>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  The work on qbottle with my investor partner continued for about another year and a half, and during that time, we successfully grew it to six-figure revenues.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">A Turning Point: Burnout and the Call of AI</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  Despite these successes, I started to feel a bit burned out. Managing e-commerce businesses and the Amazon FBA world was no longer satisfying. I realized that this wasn't aligning with my core desire to be an entrepreneur focused on building my own visions. I had initially gone into consulting and agency work out of necessity after my first FBA business didn't work out. Now, I was mainly working for other businesses again.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  It was around this time, while working on qbottle, that I discovered GPT-3 and the whole AI hype started to build. Suddenly, it was crystal clear to me: this was the field I wanted to dive into. I wanted to leverage AI technology.
                </p>

                <div className="bg-purple-900/20 border-l-4 border-purple-400 pl-6 py-4 my-8 rounded-r-lg">
                  <h3 className="text-2xl font-bold text-purple-200 mb-4">Closing the Amazon Chapter</h3>
                  <p className="text-lg leading-[1.8] text-gray-200 mb-6">
                    And so, this part of my journey reached its end. I decided to leave everything related to e-commerce behind. I closed my agency and parted ways with my qbottle partner to fully dedicate myself to exploring and building in the world of AI.
                  </p>
                </div>

                <p className="text-xl leading-[1.8] text-gray-200 font-medium">
                  The Amazon FBA chapter was a rollercoaster of launching, learning, losing, and then leveraging those hard-won lessons to help others and build again. It taught me an incredible amount and ultimately helped clarify what I truly wanted to pursue next.
                </p>
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AmazonFBA;
